// src/pages/Dashboard.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  UserPlus, IndianRupee, Calendar, Users, Briefcase, Search, Phone, Mail, MapPin,
  BookOpen, ChevronLeft, ChevronRight, TrendingUp, AlertCircle, CheckCircle, Clock,
  GraduationCap
} from 'lucide-react';
import SummaryApi from '../common/index';

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [todayRegistrations, setTodayRegistrations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(5);
  const [stats, setStats] = useState([]);

  const displayValue = (value) => {
    if (value === null || value === undefined || value === '' || value === '0' || value === 0) {
      return 'N/A';
    }
    return value;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "N/A") return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return "N/A";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return "N/A";
    }
  };

  const formatTime = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return "N/A";
    }
  };

  const safeNumber = (val) => {
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };

  useEffect(() => {
    const adminId = sessionStorage.getItem('admin_id');
    const memberType = sessionStorage.getItem('member_type');
    if (!adminId) {
      toast.error("Please login again");
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const isAdmin = memberType === 'Admin';

        // Fetch dashboard counts/stats using GET method
        const statsRes = await fetch(SummaryApi.dashboardcounts.url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!statsRes.ok) throw new Error(`Stats fetch failed: ${statsRes.status}`);
        const statsJson = await statsRes.json();

        let summaryData = {
          today_students: 0,
          total_students: 0,
          total_fee_collected: 0,
          total_billing_students: 0,
          total_admissions: 0,
          today_admissions: 0,
          total_consultants: 0
        };

        if (statsJson.status && statsJson.data?.summary) {
          summaryData = {
            today_students: statsJson.data.summary.today_students || 0,
            total_students: statsJson.data.summary.total_students || 0,
            total_fee_collected: statsJson.data.summary.total_fee_collected || 0,
            total_billing_students: statsJson.data.summary.total_billing_students || 0,
            total_admissions: statsJson.data.summary.total_admissions || 0,
            today_admissions: statsJson.data.summary.today_admissions || 0,
            total_consultants: 0
          };
        }

        const baseStats = [
          {
            title: "Today's Registrations",
            value: safeNumber(summaryData.today_students).toLocaleString('en-IN'),
            icon: UserPlus,
            gradient: "from-emerald-500 via-green-500 to-emerald-600",
          },
          {
            title: "Total Students",
            value: safeNumber(summaryData.total_students).toLocaleString('en-IN'),
            icon: Users,
            gradient: "from-blue-500 via-indigo-500 to-blue-600",
          },
          {
            title: "Fees Collected Total",
            value: `₹${safeNumber(summaryData.total_fee_collected).toLocaleString('en-IN')}`,
            icon: IndianRupee,
            gradient: "from-purple-500 via-violet-500 to-violet-600",
          },
          {
            title: "Total Paid Students",
            value: safeNumber(summaryData.total_billing_students).toLocaleString('en-IN'),
            icon: Calendar,
            gradient: "from-amber-500 via-orange-500 to-amber-600",
          },
          {
            title: "Admissions",
            value: (
              <div className="flex flex-col items-start w-full gap-0.5">
                <div className="flex items-center justify-between w-full">
                  <span className="text-xs font-medium opacity-90">Today Admissions</span>
                  <span className="text-lg font-black">{safeNumber(summaryData.today_admissions).toLocaleString('en-IN')}</span>
                </div>
                <div className="w-full h-px bg-white/20 my-0.5"></div>
                <div className="flex items-center justify-between w-full">
                  <span className="text-xs font-medium opacity-90">Total Admissions</span>
                  <span className="text-lg font-black">{safeNumber(summaryData.total_admissions).toLocaleString('en-IN')}</span>
                </div>
              </div>
            ),
            icon: GraduationCap,
            gradient: "from-rose-500 via-pink-500 to-rose-600",
          }
        ];

        if (isAdmin) {
          baseStats.push({
            title: "Total Consultants",
            value: "0",
            icon: Briefcase,
            gradient: "from-cyan-500 via-teal-500 to-cyan-600",
          });
        }

        setStats(baseStats);

        // Today's registrations API - GET without any parameters
        const todayRes = await fetch(SummaryApi.getTodayRegistrations.url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!todayRes.ok) {
          throw new Error(`Today's registrations fetch failed: ${todayRes.status}`);
        }

        const todayJson = await todayRes.json();

        if (todayJson.status && Array.isArray(todayJson.data)) {
          if (todayJson.data.length === 0) {
            setTodayRegistrations([]);
            return;
          }

          // Format the data based on the API response structure
          const formatted = todayJson.data.map((item) => {
            return {
              id: item.student_id,
              name: item.student_name || 'N/A',
              father_name: item.father_name || 'N/A',
              mother_name: item.mother_name || 'N/A',
              caste: item.caste || 'N/A',
              religion: item.religion || 'N/A',
              mobile: item.mobile_number || 'N/A',
              alternate_mobile: item.alternate_mobile_number || 'N/A',
              email: item.email || 'N/A',
              degree: item.degree || 'N/A',
              stream: item.stream || 'N/A',
              academic_year: item.academic_year || 'N/A',
              preferred_branch: item.stream || item.degree || 'N/A',
              dob: formatDate(item.date_of_birth),
              aadhar: item.aadhar || 'N/A',
              address: [item.village, item.city, item.district, item.state, item.pincode]
                .filter(Boolean)
                .join(', ') || 'N/A',
              state: item.state || 'N/A',
              district: item.district || 'N/A',
              city: item.city || 'N/A',
              village: item.village || 'N/A',
              pincode: item.pincode || 'N/A',
              registration_date: formatDate(item.created_date),
              registration_time: formatTime(item.created_date),
              utr_number: item.utr_number || 'N/A',
              transaction_id: item.transaction_id || 'N/A',
              payment_receipt: item.payment_receipt || 'N/A',
              amount: item.amount ? `₹${safeNumber(item.amount).toLocaleString('en-IN')}` : 'N/A',
              amount_value: item.amount || 0,
              is_paid: item.is_paid || "0",
              payment_date: item.payment_date ? formatDateTime(item.payment_date) : 'N/A',
              consultancy_name: item.consultancy_name || 'Direct',
              is_active: item.is_active || "1",
              admission_done: item.admission_done || "0",
            };
          });

          setTodayRegistrations(formatted.reverse());
        } else {
          setTodayRegistrations([]);
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
        toast.error("Failed to load dashboard data");

        const isAdmin = memberType === 'Admin';
        const fallbackStats = [
          { title: "Today's Registrations", value: "0", icon: UserPlus, gradient: "from-emerald-500 via-green-500 to-emerald-600" },
          { title: "Total Students", value: "0", icon: Users, gradient: "from-blue-500 via-indigo-500 to-blue-600" },
          { title: "Fees Collected Total", value: "₹0", icon: IndianRupee, gradient: "from-purple-500 via-violet-500 to-violet-600" },
          { title: "Total Paid Students", value: "0", icon: Calendar, gradient: "from-amber-500 via-orange-500 to-amber-600" },
          { 
            title: "Admissions", 
            value: (
              <div className="flex flex-col items-start w-full gap-0.5">
                <div className="flex items-center justify-between w-full">
                  <span className="text-xs font-medium opacity-90">Today Admissions</span>
                  <span className="text-lg font-black">0</span>
                </div>
                <div className="w-full h-px bg-white/20 my-0.5"></div>
                <div className="flex items-center justify-between w-full">
                  <span className="text-xs font-medium opacity-90">Total Admissions</span>
                  <span className="text-lg font-black">0</span>
                </div>
              </div>
            ), 
            icon: GraduationCap, 
            gradient: "from-rose-500 via-pink-500 to-rose-600" 
          },
        ];

        if (isAdmin) {
          fallbackStats.push({
            title: "Total Consultants",
            value: "0",
            icon: Briefcase,
            gradient: "from-cyan-500 via-teal-500 to-cyan-600",
          });
        }

        setStats(fallbackStats);
        setTodayRegistrations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const filteredRegistrations = todayRegistrations.filter((student) => {
    const query = searchQuery.toLowerCase();
    return (
      (student.name?.toLowerCase() || "").includes(query) ||
      (student.mobile?.toString() || "").includes(query) ||
      (student.email?.toLowerCase() || "").includes(query)
    );
  });

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRegistrations.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredRegistrations.length / recordsPerPage);

  const isAdmin = sessionStorage.getItem('member_type') === 'Admin';

  const handlePayNow = (student) => {
    if (!student.id) {
      toast.error("Cannot process payment: Student ID missing");
      return;
    }
    const encodedId = btoa(student.id.toString());
    const encodedName = btoa((student.name || 'Unknown Student').trim());
    navigate(`/create-studentfee?id=${encodedId}&name=${encodedName}`);
  };

  // Function to handle admission status update
  const handleAdmissionStatus = async (studentId, currentStatus) => {
    try {
      const newStatus = currentStatus === "1" ? "0" : "1";
      const response = await fetch(SummaryApi.updateAdmissionStatus.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: studentId,
          admission_status: newStatus
        })
      });

      if (!response.ok) throw new Error('Failed to update admission status');
      
      const result = await response.json();
      if (result.status) {
        toast.success(`Admission status updated successfully`);
        // Update local state
        setTodayRegistrations(prev => 
          prev.map(student => 
            student.id === studentId 
              ? { ...student, admission_done: newStatus }
              : student
          )
        );
        
        // Refetch stats to update the admissions count
        const statsRes = await fetch(SummaryApi.dashboardcounts.url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (statsRes.ok) {
          const statsJson = await statsRes.json();
          if (statsJson.status && statsJson.data?.summary) {
            const summaryData = {
              today_students: statsJson.data.summary.today_students || 0,
              total_students: statsJson.data.summary.total_students || 0,
              total_fee_collected: statsJson.data.summary.total_fee_collected || 0,
              total_billing_students: statsJson.data.summary.total_billing_students || 0,
              total_admissions: statsJson.data.summary.total_admissions || 0,
              today_admissions: statsJson.data.summary.today_admissions || 0,
              total_consultants: 0
            };
            
            // Update stats with new admissions data
            setStats(prevStats => 
              prevStats.map(stat => 
                stat.title === "Admissions" 
                  ? {
                      ...stat,
                      value: (
                        <div className="flex flex-col items-start w-full gap-0.5">
                          <div className="flex items-center justify-between w-full">
                            <span className="text-xs font-medium opacity-90">Today Admissions</span>
                            <span className="text-lg font-black">{safeNumber(summaryData.today_admissions).toLocaleString('en-IN')}</span>
                          </div>
                          <div className="w-full h-px bg-white/20 my-0.5"></div>
                          <div className="flex items-center justify-between w-full">
                            <span className="text-xs font-medium opacity-90">Total Admissions</span>
                            <span className="text-lg font-black">{safeNumber(summaryData.total_admissions).toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      )
                    }
                  : stat
              )
            );
          }
        }
      } else {
        toast.error(result.message || 'Failed to update admission status');
      }
    } catch (error) {
      console.error('Error updating admission status:', error);
      toast.error('Failed to update admission status');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4 md:p-6 space-y-6">
      {loading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white/60 backdrop-blur-sm rounded-lg shadow border border-white/30 p-4 text-center">
        <h1 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-gray-900 to-slate-800 bg-clip-text text-transparent">
          Dashboard
        </h1>
      </div>

      {/* Stats Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${stats.length === 6 ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-4`}>
        {stats.map((card, i) => (
          <div
            key={i}
            className={`group overflow-hidden rounded-xl shadow-md border bg-gradient-to-br ${card.gradient} text-white p-5 transition-all hover:scale-[1.03]`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 rounded-lg bg-white/20 backdrop-blur-md border border-white/30">
                <card.icon size={24} strokeWidth={2.5} />
              </div>
            </div>
            <p className="text-sm font-semibold opacity-80 mb-2">{card.title}</p>
            <div className="text-2xl font-black tracking-tight">{card.value}</div>
          </div>
        ))}
      </div>

      {/* Today's Registrations Table */}
      <div className="bg-white/70 backdrop-blur-xl rounded-xl shadow-lg border border-white/30 overflow-hidden">
        <div className="bg-slate-900 p-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-emerald-500 rounded-lg text-white">
              <TrendingUp size={20} />
            </div>
            <h3 className="text-xl font-bold text-white">Today's Registrations</h3>
          </div>
          <div className="relative w-full md:w-96">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search Name, Mobile or Email"
              className="w-full pl-10 pr-4 py-2 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 font-bold text-slate-700">S.No</th>
                <th className="px-4 py-3 font-bold text-slate-700">Student Details</th>
                <th className="px-4 py-3 font-bold text-slate-700">Contact Info</th>
                <th className="px-4 py-3 font-bold text-slate-700">Education Details</th>
                {isAdmin && <th className="px-4 py-3 font-bold text-slate-700">Consultant</th>}
                <th className="px-4 py-3 font-bold text-slate-700">Registration</th>
                <th className="px-4 py-3 font-bold text-slate-700">Payment Status / Action</th>
                <th className="px-4 py-3 font-bold text-slate-700">Admission Status</th>
                {isAdmin && <th className="px-4 py-3 font-bold text-slate-700">Transaction Details</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentRecords.length > 0 ? (
                currentRecords.map((student, index) => (
                  <tr key={student.id} className="hover:bg-emerald-50/50 bg-white/40 transition-colors">
                    <td className="px-4 py-4 font-bold text-emerald-700">
                      {(currentPage - 1) * recordsPerPage + index + 1}
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-bold text-slate-900">{displayValue(student.name)}</div>
                      <div className="text-xs text-slate-500">
                        <BookOpen size={10} className="inline mr-1"/>DOB: {displayValue(student.dob)}
                      </div>
                      <div className="text-xs text-slate-400">Aadhar: {displayValue(student.aadhar)}</div>
                      <div className="text-xs text-slate-400">Father: {displayValue(student.father_name)}</div>
                      <div className="text-xs text-slate-400">Mother: {displayValue(student.mother_name)}</div>
                      <div className="text-xs text-slate-400">Caste: {displayValue(student.caste)}</div>
                      <div className="text-xs text-slate-400">Religion: {displayValue(student.religion)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center text-slate-700 font-medium">
                        <Phone size={12} className="mr-1 text-slate-400 shrink-0"/>
                        {displayValue(student.mobile)}
                      </div>
                      {student.alternate_mobile !== 'N/A' && (
                        <div className="flex items-center text-xs text-slate-500 mt-1">
                          <Phone size={10} className="mr-1 text-slate-400 shrink-0"/>
                          Parent Mobile: {displayValue(student.alternate_mobile)}
                        </div>
                      )}
                      <div className="flex items-center text-xs text-slate-500 mt-1">
                        <Mail size={12} className="mr-1 text-slate-400 shrink-0"/>
                        <span className="truncate max-w-[150px]">{displayValue(student.email)}</span>
                      </div>
                      <div className="flex items-start text-xs text-slate-500 mt-1">
                        <MapPin size={12} className="mr-1 text-slate-400 shrink-0 mt-0.5"/>
                        <span className="line-clamp-2">{displayValue(student.address)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium text-slate-800">{displayValue(student.degree)}</div>
                      <div className="text-xs text-slate-600 font-semibold">{displayValue(student.stream)}</div>
                      <div className="text-xs text-slate-500">Year: {displayValue(student.academic_year)}</div>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-4">
                        <div className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-bold border border-blue-100">
                          <Briefcase size={10} className="mr-1"/>
                          {displayValue(student.consultancy_name)}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <div className="font-medium text-slate-900">{displayValue(student.registration_date)}</div>
                      <div className="text-xs text-slate-500">{displayValue(student.registration_time)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col items-start gap-2">
                        {student.is_paid == "1" ? (
                          <div className="flex flex-col items-start gap-1">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                              <CheckCircle size={14} className="mr-1" />
                              Paid
                            </span>
                            <span className="text-xs font-semibold text-emerald-700">Amount: {student.amount}</span>
                            {student.payment_date !== 'N/A' && (
                              <span className="text-xs text-slate-500">Paid on: {student.payment_date}</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-start gap-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                              <Clock size={14} className="mr-1" />
                              Pending
                            </span>
                            <button
                              onClick={() => handlePayNow(student)}
                              className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 rounded transition shadow-sm"
                            >
                              Pay Now
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col items-start gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            student.admission_done == "1" 
                              ? 'bg-green-100 text-green-800 border border-green-200'
                              : 'bg-gray-100 text-gray-800 border border-gray-200'
                          }`}>
                            {student.admission_done === "1" ? (
                              <>
                                <CheckCircle size={14} className="mr-1" />
                                Admitted
                              </>
                            ) : (
                              <>
                                <Clock size={14} className="mr-1" />
                                Not Admitted
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    </td>
                    {isAdmin && student.is_paid == "1" && (
                      <td className="px-4 py-4 text-sm">
                        <div className="space-y-1">
                          <div>
                            <span className="font-medium text-slate-700">UTR:</span>{' '}
                            <span className="text-xs font-mono">{displayValue(student.utr_number)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Txn ID:</span>{' '}
                            <span className="text-xs font-mono">{displayValue(student.transaction_id)}</span>
                          </div>
                          {student.payment_receipt !== 'N/A' && (
                            <div>
                              <span className="font-medium text-slate-700">Receipt:</span>{' '}
                              <span className="text-xs text-blue-600">{displayValue(student.payment_receipt)}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                    {isAdmin && student.is_paid != "1" && (
                      <td className="px-4 py-4 text-sm">
                        <span className="text-slate-400">Not paid yet</span>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isAdmin ? 9 : 8} className="px-4 py-16 text-center bg-white/30">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="p-4 bg-slate-100 rounded-full text-slate-400">
                        <AlertCircle size={48} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-lg font-bold text-slate-600">No Registrations Found</p>
                        <p className="text-sm text-slate-400">No student records match your search or today's list.</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredRegistrations.length > 0 && (
          <div className="p-4 border-t flex flex-col md:flex-row justify-between items-center gap-4 bg-white/50">
            <p className="text-sm text-slate-600">
              Showing <span className="font-semibold">{indexOfFirstRecord + 1}</span> to{' '}
              <span className="font-semibold">{Math.min(indexOfLastRecord, filteredRegistrations.length)}</span> of{' '}
              <span className="font-semibold">{filteredRegistrations.length}</span>
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border rounded-lg bg-white disabled:opacity-50 hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-8 h-8 rounded-lg font-medium transition-all ${
                    currentPage === i + 1
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'bg-white border text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border rounded-lg bg-white disabled:opacity-50 hover:bg-slate-50 transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}